import { useState } from "react"

import { Space, Typography } from "antd"
import { Cell, Legend, Pie, PieChart, Sector } from "recharts"

const { Text } = Typography


interface AnalyticsPieChartProps {
  chartWidth: number
  chartHeight: number
  data?: any[]
  dataKey: string
  name: string
  nameKey: string
  fill: string
  fillCells?: string[]
  isComparison?: boolean
  filtersText?: string | string[] | undefined | null
}


export const AnalyticsPieChart = (props: AnalyticsPieChartProps) => {
  const {
    chartWidth,
    chartHeight,
    data,
    dataKey,
    name,
    nameKey,
    fill,
    fillCells,
    isComparison } = props
  const [activePieIndex, setActivePieIndex] = useState<number>()

  // Расчет процентов для режима сравнения
  const getPercentages = () => {
    if (!data || data.length === 0) return []
    const total = data.reduce((sum, item) => sum + (item[dataKey] || 0), 0)
    return data.map(item => ({
      ...item,
      percentage: total > 0 ? ((item[dataKey] / total) * 100).toFixed(2) : "0.00"
    }))
  }

  const dataWithPercentages = isComparison ? getPercentages() : data
  const renderCustomLabel = (entry: any) => {
    if (!isComparison) return null
    return `${entry[dataKey]} - ${entry.percentage}%`
  }

  const renderActiveShape = (props: any) => {
    const RADIAN = Math.PI / 180
    const {
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      period,
      value } = props
    const sin = Math.sin(-RADIAN * midAngle)
    const cos = Math.cos(-RADIAN * midAngle)
    const sx = cx + (outerRadius + 12) * cos
    const sy = cy + (outerRadius + 12) * sin
    // extend connector further out on hover so label sits farther from sector
    const mx = cx + (outerRadius + 60) * cos
    const my = cy + (outerRadius + 60) * sin
    const ex = mx + (cos >= 0 ? 1 : -1) * 32
    const ey = my
    const textAnchor = cos >= 0 ? "start" : "end"

    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 10}
          fill={fill}
        />
        <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />
        <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
        <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} textAnchor={textAnchor} fill="#333">{period}</text>
        <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} dy={18} textAnchor={textAnchor} fill="#999">
          {value}
        </text>
      </g>
    )
  }


  const onPieEnter = (_: any, index: number) => {
    setActivePieIndex(index)
  }

  return (
    <div>
      <PieChart width={chartWidth} height={chartHeight}>
        <Pie
          data={dataWithPercentages}
          dataKey={dataKey}
          name={name}
          nameKey={nameKey}
          fill={fill}
          outerRadius={125}
          label={isComparison ? renderCustomLabel : activePieIndex === undefined}
          activeIndex={activePieIndex}
          activeShape={renderActiveShape}
          onMouseEnter={onPieEnter}
          onMouseLeave={() => setActivePieIndex(undefined)}
        >
          {fillCells && fillCells.length === dataWithPercentages?.length && fillCells?.map((x) => (
              <Cell key={`${dataKey}${x}`} fill={x} />
            ))}
        </Pie>
        {!isComparison && nameKey && <Legend verticalAlign="bottom" height={36} />}
      </PieChart>
      {isComparison && (
        <div style={{ marginTop: "10px", display: "flex", justifyContent: "center", gap: "24px", flexWrap: "wrap" }}>
          {dataWithPercentages?.map((item, index) => {
            const itemColor = fillCells?.[index] || "#1890ff"
            return (
              <Space key={crypto.randomUUID()} direction="vertical" >
                <Space key={crypto.randomUUID()} size={8} align="center" >
                  <div
                    style={{
                      width: "12px",
                      height: "12px",
                      backgroundColor: itemColor,
                      borderRadius: "2px"
                    }}
                  />
                  <Text strong>{`${item.period} (${item.percentage}%)`}</Text>
                </Space>
                {item.filterText &&
                  <Text style={{ fontSize: 12 }}>{`${item.filterText}`} </Text>
                }
              </Space>
            )
          })}
        </div>
      )}
    </div>
  )
}